import { Link } from 'react-router-dom';
import type { Subscription } from '../../types';
import Card from '../common/Card';
import StatusBadge from '../common/StatusBadge';
import { useAuth } from '../../hooks/useAuth';
import { canAccessBilling } from '../../utils/permissions';
import { deviceLimitState, primaryHostname, upgradeHref } from '../../utils/subscription';

interface Props {
  subscription: Subscription;
  showViewDetails?: boolean;
  showOpenController?: boolean;
}

export default function ServiceCard({ subscription, showViewDetails = true, showOpenController = true }: Props) {
  const { user } = useAuth();
  const canUpgrade = canAccessBilling(user);

  // Prefer the customer's custom domain, then the customer-facing name (UOS console name or a
  // node UISP's netconsole.cloud name, design §20), then the hostname.
  const displayHostname = subscription.custom_hostnames?.[0] || primaryHostname(subscription);

  // Controller URL: UOS routes don't use a port; legacy UniFi uses 8443
  const controllerUrl = subscription.migrated_to_uos && subscription.uos_hostname
    ? `https://${displayHostname}`
    : `https://${displayHostname}${subscription.app_name === 'UniFi' ? ':8443' : ''}`;

  // device_count/max_devices can each be null independently — only render
  // limit messaging when both are known.
  const { device_count: deviceCount, max_devices: maxDevices } = subscription;
  const limitState =
    deviceCount !== null && maxDevices !== null ? deviceLimitState(deviceCount, maxDevices) : 'ok';
  const isOverLimit = limitState === 'over';
  const isNearLimit = limitState === 'near';
  const upgradeLinkHref = upgradeHref(subscription.id);

  return (
    <Card className="p-6">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {displayHostname}
          </h3>
          <div className="flex items-center gap-2">
            <p className="text-sm text-gray-500">{subscription.app_name}</p>
            {subscription.migrated_to_uos && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                UOS
              </span>
            )}
          </div>
        </div>
        <StatusBadge status={subscription.status} />
      </div>

      <div className="space-y-2 text-sm text-gray-600 mb-4">
        {subscription.plan && (
          <div className="flex justify-between">
            <span>Plan:</span>
            <span className="font-medium text-gray-900">{subscription.plan}</span>
          </div>
        )}
        {subscription.device_count !== null && (
          <div className="flex justify-between items-center">
            <span>Devices:</span>
            <span className="flex items-center gap-2">
              <span className={`font-medium ${
                isOverLimit ? 'text-red-600' : isNearLimit ? 'text-amber-600' : 'text-gray-900'
              }`}>
                {subscription.device_count.toLocaleString()}
                {subscription.max_devices !== null && `/${subscription.max_devices.toLocaleString()}`}
              </span>
              {(isOverLimit || isNearLimit) && (
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      isOverLimit ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {isOverLimit ? 'Over limit' : 'Near limit'}
                  </span>
                  {canUpgrade && (
                    <Link
                      to={upgradeLinkHref}
                      aria-label={`Upgrade plan for ${displayHostname}`}
                      className="text-xs text-blue-600 hover:text-blue-800 underline font-medium"
                    >
                      Upgrade
                    </Link>
                  )}
                </span>
              )}
            </span>
          </div>
        )}
        {subscription.software_version && (
          <div className="flex justify-between">
            <span>Version:</span>
            <span className="font-medium text-gray-900">
              {subscription.software_version}
            </span>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        {showViewDetails && (
          <Link
            to={`/services/${subscription.id}`}
            aria-label={`View details for ${displayHostname}`}
            className="flex-1 text-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
          >
            View Details
          </Link>
        )}
        {showOpenController && (
          <a
            href={controllerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 text-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
          >
            {subscription.app_name === 'UISP' ? 'Open UISP' : 'Open Controller'}
          </a>
        )}
      </div>
    </Card>
  );
}
